/**
 * SOC 2 and ISO 42001 on Today — V106-1-8 / V106-5-6 (design pass
 * `v106-program-screens`, D8; ADR-0071 amendment).
 *
 * One DS StatusCard per program from GET /today/programs (the server composes
 * readiness, the shared next gap, the weekly delta from readiness_snapshots,
 * pace and the D8 overrides). The large number is chosen by stage (D8):
 * readiness in Prepare (with pace, the 3-part meter and "✕ N not met"), the
 * dated problem in Operate ("12 days late"), a short noun phrase for "Needs
 * you" / "Needs setup". An override (Class A expired / opinion / report not on
 * file) sits in the next-step slot as a status-coloured strip with the gap under
 * it ("Then · …"). Only the top-ranked card fills its button (`primary`).
 *
 * Reads: /today/programs (any member), members (names). Writes: "Ask <admin> to
 * approve" records an `ask` entry (any member except viewers) and opens a mail.
 */
import { useRef, useState, type ReactNode } from 'react';
import { focusWhenReady } from '../programs/objective-parts';
import { Link } from 'react-router-dom';
import { StatusCard } from '@aigovops/design-system';
import { recordProgramEntry, type ProgramTodayCard } from '../../lib/program-screens-api';
import { fmtDate, todayCard, type TodayCardModel } from '../../lib/program-screens-model';
import '../../pages/program-screens.css';

const STATUS_WORD: Record<TodayCardModel['status'], string> = { on_track: 'On track', at_risk: 'At risk', off_track: 'Off track', needs_you: 'Needs you', needs_setup: 'Needs setup' };

export function ObjectiveTodayCard({ card, model, slug, prefix, primary, email, onAsked, setupStep }: {
  card: ProgramTodayCard; model: TodayCardModel; slug: string; prefix: string; primary: boolean; email?: string | null; onAsked?: (m: string) => void;
  /** V108-8-16: the program's own next setup step (a quiet link, `ProgramSetupStep`). */
  setupStep?: ReactNode;
}) {
  const [asked, setAsked] = useState<{ name: string; at: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const askedRef = useRef<HTMLSpanElement>(null);
  const today = new Date().toISOString().slice(0, 10);
  const stageTab = card.variant === 'needs_you' ? 'scope' : card.variant === 'operate_problem' || card.stage === 'operate' ? 'operate' : 'prepare';
  const href = `${prefix}/programs/${card.program}/${stageTab}`;
  const m = model;
  const pace = m.pace;
  const details = m.metric.big && m.meter ? (
    <>
      {pace ? (
        <div className="v6-pace" data-part="pace">
          {pace.kind === 'switch' ? <><span>{`Counting changed ${pace.on}`}</span>{pace.needed ? <span>needs <b>{`~${pace.needed} a week`}</b></span> : null}</>
            : pace.kind === 'delta' ? (pace.behind
              // Glance M (round 2): when behind, the number is neutral and the verdict is a word in the status colour.
              ? <span>{`${pace.gained >= 0 ? '+' : ''}${pace.gained} of ~${pace.needed} needed this week · `}<span className="v6-behind">behind</span></span>
              : <span><span className="v6-ahead"><span aria-hidden="true">{pace.gained >= 0 ? '▲ ' : '▼ '}</span>{`${pace.gained >= 0 ? '+' : ''}${pace.gained}`}</span>{` of ~${pace.needed} needed this week`}</span>)
              : <span>{pace.gained !== null ? <><span className="v6-ahead"><span aria-hidden="true">{pace.gained >= 0 ? '▲ ' : '▼ '}</span>{`${pace.gained >= 0 ? '+' : ''}${pace.gained}`}</span>{` this week · ${pace.open} open`}</> : `${pace.open} open`}</span>}
        </div>
      ) : null}
      <div className="v6-meter-wrap" aria-hidden="true">
        <div className="v6-meter">
          {m.meter.met ? <span className="v6-m" style={{ flexGrow: m.meter.met }} /> : null}
          {m.meter.notMet ? <span className="v6-n" style={{ flexGrow: m.meter.notMet }} /> : null}
          {m.meter.open ? <span className="v6-o" style={{ flexGrow: m.meter.open }} /> : null}
        </div>
        {m.meter.target !== null ? <span className="v6-tick" data-part="target-tick" title={`This week's target: ${m.meter.target}`}
          style={{ left: `${Math.max(0, Math.min(100, (m.meter.target / Math.max(1, m.meter.met + m.meter.notMet + m.meter.open)) * 100))}%` }} /> : null}
      </div>
      {m.notMet ? <span className={`v6-count${m.notMet ? '' : ' v6-count--zero'}`} data-part="not-met">{m.notMet ? <><span aria-hidden="true">✕</span>{` ${m.notMet} not met${m.inProgress ? ` · ${m.inProgress} ticketed` : ''}`}</> : '0 not met'}</span> : null}
    </>
  ) : m.consequence || m.note ? <>
    {m.consequence ? <p className="v6-consequence" data-tone={m.status === 'needs_setup' ? 'warn' : undefined}>{m.consequence}</p> : null}
    {m.note ? <p className="v6-also" data-part="note">{m.note}</p> : null}
  </> : null;
  const next = m.override || m.next || m.review || m.also || m.revert || m.secondary || asked || err || setupStep ? (
    <div className="v6-next-inner">
      {m.override ? <div className="v6-override" data-tone={m.override.tone} data-part="override"><b>{m.override.headline}</b><span>{m.override.dated}</span><span className="v6-cq">{m.override.consequence}</span>
        {m.override.action ? <Link className={`gp-linkbtn gp-linkbtn--compact v6-ov-act${primary ? ' btn-primary' : ''}`} to={m.override.action.href} data-part="override-action">{m.override.action.label}</Link> : null}</div> : null}
      {m.next ? (
        <Link className="v6-next-line" to={m.next.href.replace(/^https?:\/\/[^/]+/, '')} aria-label={m.next.ariaLabel} title={m.next.title} data-part="next-line">
          {/* Glance M (round 2): key and statement on one run so the negation sticks. */}
          <span className="v6-tx"><span className="v6-k" data-tone={m.next.tone ?? undefined}>{m.next.tone === 'bad' ? <span aria-hidden="true">✕ </span> : null}{`${m.next.key}:`}</span>{` ${m.next.text}`}</span>
        </Link>
      ) : null}
      {/* D-106-18 (glance G1): results waiting on this reviewer are a keyed line under the gap, not an "Also". */}
      {m.review ? (
        <Link className="v6-next-line" to={m.review.href} data-part="review-line" aria-label={`Review: ${m.review.text}`}>
          <span className="v6-tx"><span className="v6-k">Review:</span>{` ${m.review.text}`}</span>
        </Link>
      ) : null}
      {m.also ? <p className="v6-also">Also: <Link to={m.also.href}>{m.also.text}</Link></p> : null}
      {m.revert ? <p className="v6-also"><Link to={m.revert.href} data-part="revert">{m.revert.text}</Link></p> : null}
      {asked ? <span className="v6-asked" role="status" ref={askedRef} tabIndex={-1} data-part="asked"><span aria-hidden="true">✓</span>{` Asked ${asked.name} · ${fmtDate(asked.at, today)}`}</span> : null}
      {m.secondary ? <p className="v6-also"><Link to={m.secondary.href}>{m.secondary.text}</Link></p> : null}
      {setupStep}
      {err ? <p className="v6-err" role="alert">{err}</p> : null}
    </div>
  ) : undefined;
  const ask = m.action.kind === 'ask' ? m.action : null;
  const action = ask && !asked ? {
    label: ask.label, href, onClick: () => {
      void (async () => {
        try {
          await recordProgramEntry(slug, card.program, { kind: 'ask', subject_key: ask.askUserId, data: { about: ask.about } });
          if (email) { try { window.open(`mailto:${email}?subject=${encodeURIComponent(`GAC Pro: ${ask.about}`)}`, '_self'); } catch { /* no mail client */ } }
          const name = ask.label.replace(/^Ask /, '').replace(/ to approve$/, '');
          setAsked({ name, at: new Date().toISOString() });
          onAsked?.(`Asked ${name}`);
          // A11y H (round 2): the button is replaced; focus the confirmation, never <body>.
          focusWhenReady(() => askedRef.current);
        } catch (e) { setErr(e instanceof Error ? e.message : 'The ask could not be sent.'); }
      })();
    },
  } : ask && asked ? { label: `Open ${m.title}`, href, variant: 'link' as const }
    // With the override's own button in the strip, the gap action is a quiet link (one filled button per card).
    : { label: m.action.label, href: (m.action as { href: string }).href, ...(m.override?.action ? { variant: 'link' as const } : {}) };
  // A11y H (build review): the card link's name carries what the card shows, not just the title and number.
  const paceText = !pace ? '' : pace.kind === 'switch' ? `counting changed ${pace.on}${pace.needed ? `, needs about ${pace.needed} a week` : ''}`
    : pace.kind === 'delta' ? `${pace.gained >= 0 ? 'up' : 'down'} ${Math.abs(pace.gained)} this week of about ${pace.needed} needed, ${pace.behind ? 'behind' : 'ahead'}` : pace.gained !== null ? `${pace.gained >= 0 ? 'up' : 'down'} ${Math.abs(pace.gained)} this week, ${pace.open} open` : `${pace.open} open`;
  const label = [
    `${m.title}: ${STATUS_WORD[m.status]}${m.statusDetail ? `, ${m.statusDetail}` : ''}`,
    `${m.metric.big} ${m.metric.small}`.trim(),
    paceText,
    m.meter?.target != null ? `this week's target ${m.meter.target}` : '',
    m.notMet !== null ? `${m.notMet} not met${m.inProgress ? `, ${m.inProgress} ticketed` : ''}` : '',
    m.consequence ?? m.note ?? '',
    m.review ? `review: ${m.review.text}` : '',
    m.date,
  ].filter(Boolean).join(' · ');
  return (
    <StatusCard
      name={m.title}
      status={m.status}
      statusDetail={m.statusDetail ?? undefined}
      eyebrow={m.eyebrow}
      metric={<>{m.metric.big}{m.metric.small ? <span className="v6-metric-small">{m.metric.small}</span> : null}</>}
      details={details}
      date={m.date}
      href={href}
      nextStep={next}
      action={action}
      primary={primary}
      aria-label={label}
    />
  );
}

export { todayCard };
